/**
 * Page `/annuler/:id` — atterrissage du bouton "Annuler ma préréservation"
 * du mail de confirmation.
 *
 * Différence avec /confirmer : on **ne déclenche pas** l'annulation
 * automatiquement. On charge le récap (`GET /preregistrations/:id`) et on
 * demande une **confirmation explicite** avec un bouton rouge dédié. Si la
 * résa a déjà reçu un paiement (`paid_cents > 0`), on affiche un
 * avertissement clair "remboursement à voir avec le club".
 *
 * Si l'utilisateur arrive ici sur une résa déjà annulée → message neutre.
 */
import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  extractApiError,
  fetchPreregistrationSummary,
  publicCancelPreregistration,
} from '../api.js';

function formatEur(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function CancelPreregistration() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Garde une "snapshot" du paid_cents au moment du chargement, pour pouvoir
  // afficher dans l'écran final "X € déjà perçus, contactez le club".
  const [paidSnapshot, setPaidSnapshot] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchPreregistrationSummary(id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setPaidSnapshot(d.paid_cents ?? 0);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        setLoadError(
          code === 404
            ? 'Cette préréservation est introuvable. Vérifiez votre lien ou contactez le club.'
            : extractApiError(err, 'Impossible de charger la préréservation.'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleConfirm() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const updated = await publicCancelPreregistration(id);
      setData(updated);
      setDone(true);
    } catch (err) {
      setSubmitError(extractApiError(err, "Impossible d'annuler pour le moment."));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="sm">
        <Alert severity="warning" sx={{ mb: 2 }}>{loadError}</Alert>
        <Button component={RouterLink} to="/" variant="outlined" startIcon={<HomeOutlinedIcon />}>
          Retour à l'accueil
        </Button>
      </Container>
    );
  }

  if (!data) return null;

  const alreadyCancelled = data.payment_status === 'cancelled';
  const hasPaid = paidSnapshot > 0;

  // === État final après annulation ==================================
  if (done) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider' }}>
          <Stack spacing={3} alignItems="center">
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Préréservation annulée
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Votre place est libérée. Merci de nous avoir prévenus !
              </Typography>
            </Box>
            {hasPaid && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Vous aviez réglé {formatEur(paidSnapshot)}
                </Typography>
                <Typography variant="body2">
                  Le club vous recontactera pour le remboursement. Si vous n'avez pas de retour
                  sous quelques jours, n'hésitez pas à répondre au mail de confirmation initial
                  ou à appeler le 07 81 77 93 97.
                </Typography>
              </Alert>
            )}
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<HomeOutlinedIcon />}
              fullWidth
            >
              Retour à l'accueil
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // === Résa déjà annulée (idempotence) ==============================
  if (alreadyCancelled) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cette préréservation est <strong>déjà annulée</strong>. Si c'est une erreur,
            contactez le club.
          </Alert>
          <Button component={RouterLink} to="/" variant="outlined" startIcon={<HomeOutlinedIcon />}>
            Retour à l'accueil
          </Button>
        </Paper>
      </Container>
    );
  }

  // === Écran de confirmation =======================================
  return (
    <Container maxWidth="sm">
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider' }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="error.main" sx={{ letterSpacing: 1.2 }}>
              Annulation
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>
              Annuler la préréservation de {data.first_name} ?
            </Typography>
          </Box>

          <Box
            sx={(theme) => ({
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'action.hover',
            })}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, color: 'text.secondary' }}>
              <EventOutlinedIcon fontSize="small" />
              <Typography variant="body2">
                {data.event_name} · {formatEventDate(data.event_date)}
              </Typography>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">
              <strong>{data.adults}</strong> adulte{data.adults > 1 ? 's' : ''} ·{' '}
              <strong>{data.children}</strong> enfant{data.children > 1 ? 's' : ''} ·{' '}
              <strong>{data.meals}</strong> repas
            </Typography>
          </Box>

          {hasPaid && (
            <Alert severity="warning" icon={<WarningAmberOutlinedIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {formatEur(paidSnapshot)} déjà encaissé(s) sur cette préréservation
              </Typography>
              <Typography variant="body2">
                Vous pouvez quand même annuler depuis cette page : le club vous recontactera
                ensuite pour organiser le remboursement.
              </Typography>
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Cette action libère votre place. Si le total repasse sous la capacité, les inscriptions rouvrent. Si vous changez d'avis
            avant la date limite, vous devrez refaire une nouvelle préréservation depuis le
            site (selon les places restantes).
          </Typography>

          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              component={RouterLink}
              to={`/payer/${data.id}`}
              variant="outlined"
              fullWidth
              disabled={submitting}
            >
              Non, garder ma préréservation
            </Button>
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={<CancelOutlinedIcon />}
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? 'Annulation…' : "Oui, annuler définitivement"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
}
